<script setup lang="ts">
import { useRoute } from 'vue-router';
import { onMounted, ref, type Ref } from 'vue';
import { Stelle } from '@/models/Stelle';
import '@kanton-thurgau/design-system/dropdown';
import KpDropdown from '@kp/geofy-vuejs-lib/tg-system/components/KpDropdown.vue';
import { getI18n } from '@/helpers/i18n';

const route = useRoute();
const stelle: Ref<Stelle | null> = ref(null);
const loading: Ref<boolean> = ref(false);

onMounted(() => loadStelle(Number(route.params.id)));

async function loadStelle(id: number) {
    if (stelle.value && Number(route.params.id) === Number(id)) {
        return;
    }
    loading.value = true;
    try {
        stelle.value = null;
        stelle.value = await Stelle.loadById(Number(id));
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
    }
}

const stelleTypes = [
    { value: 'gemeinde', text: getI18n().t('gemeinde') },
    { value: 'fachstelle', text: getI18n().t('fachstelle') }
];

function mutateRecord(field: string, value: unknown) {
    stelle?.value?.set(field, value);
}

async function onSave() {
    loading.value = true;
    try {
        if (stelle?.value) {
            await stelle?.value?.save();
        }
    } finally {
        loading.value = false;
    }
}
</script>
<template>
    <div>
        <RouterLink :to="{ name: 'admin-stellen' }">
            <ktg-button appearance="tertiary" size="small" leading-icon="arrow-left"> {{ $t('back') }}</ktg-button>
        </RouterLink>
    </div>
    <form @submit.prevent="onSave">
        <div class="user_grid mt-0_25rem" role="table">
            <div>
                <ktg-labelled-choice>
                    <ktg-checkbox
                        :checked="stelle?.active !== null && stelle?.active === true"
                        input-id="active"
                        autofocus
                        @change="mutateRecord('active', $event.target?.checked)"
                    >
                    </ktg-checkbox>
                    <ktg-label for="active">{{ $t('active') }}</ktg-label>
                </ktg-labelled-choice>
            </div>
            <div></div>
            <div class="colspan-2">
                <ktg-field>
                    <ktg-label for="organisation_name">{{ $t('organisation_name') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.organisation_name"
                        input-id="organisation_name"
                        type="text"
                        class="w-full"
                        autofocus
                        @change="mutateRecord('organisation_name', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="uid">{{ $t('uid') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.uid"
                        input-id="uid"
                        type="text"
                        autofocus
                        @change="mutateRecord('uid', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="iam_id">{{ $t('iam_id') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.iam_id"
                        input-id="iam_id"
                        type="text"
                        autofocus
                        @change="mutateRecord('iam_id', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="type">{{ $t('type') }}</ktg-label>
                    <KpDropdown
                        :options="stelleTypes"
                        :model-value="stelle?.type"
                        display-field="text"
                        value-field="value"
                        @update:model-value="mutateRecord('type', $event)"
                        :emit-value-cast="String"
                        class="w-full"
                    ></KpDropdown>
                </ktg-field>
            </div>
            <div>
                <ktg-field v-if="stelle?.type !== null && stelle?.type === 'gemeinde'">
                    <ktg-label for="bfsnr">{{ $t('bfsnr') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.bfsnr"
                        input-id="bfsnr"
                        type="text"
                        autofocus
                        @change="mutateRecord('bfsnr', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="street">{{ $t('strasse') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.street"
                        input-id="street"
                        type="text"
                        autofocus
                        @change="mutateRecord('street', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="house_number">{{ $t('nr') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.house_number"
                        input-id="house_number"
                        type="text"
                        autofocus
                        @change="mutateRecord('house_number', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="post_office_box">{{ $t('postfach') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.post_office_box"
                        input-id="post_office_box"
                        type="text"
                        autofocus
                        @change="mutateRecord('post_office_box', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div></div>
            <div>
                <ktg-field>
                    <ktg-label for="swiss_zip_code_string">{{ $t('plz') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.swiss_zip_code_string"
                        input-id="swiss_zip_code_string"
                        type="text"
                        autofocus
                        @change="mutateRecord('swiss_zip_code_string', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="town">{{ $t('ort') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.town"
                        input-id="town"
                        type="text"
                        autofocus
                        @change="mutateRecord('town', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
        </div>

        <ktg-line></ktg-line>
        <h5 class="ktg-title-05 mt-1rem mb-1rem">{{ $t('ansprechperson') }}</h5>

        <div class="user_grid mt-0_25rem" role="table">
            <div>
                <ktg-field>
                    <ktg-label for="first_name">{{ $t('vorname') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.first_name"
                        input-id="first_name"
                        type="text"
                        autofocus
                        @change="mutateRecord('first_name', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="last_name">{{ $t('nachname') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.last_name"
                        input-id="last_name"
                        type="text"
                        autofocus
                        @change="mutateRecord('last_name', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>

            <div>
                <ktg-field>
                    <ktg-label for="email">{{ $t('email') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.email"
                        input-id="email"
                        type="text"
                        autofocus
                        @change="mutateRecord('email', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="phone">{{ $t('telefon') }}</ktg-label>

                    <ktg-text-input
                        :value="stelle?.phone"
                        input-id="phone"
                        type="text"
                        autofocus
                        @change="mutateRecord('phone', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
        </div>

        <div class="mt-1rem mb-1rem">
            <RouterLink :to="{ name: 'admin-stellen' }">
                <ktg-button class="cancel-button" appearance="secondary" size="small" :disabled="loading">
                    {{ $t('cancel') }}
                </ktg-button>
            </RouterLink>
            <ktg-button class="ml-1rem confirm-button" size="small" :disabled="loading" type="submit">
                {{ $t('save') }}
            </ktg-button>
        </div>
    </form>
</template>

<style scoped lang="scss">
.user_grid {
    width: 500px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    .header {
        border-bottom: 2px solid var(--ktg-color-primary-01);
    }
}
.colspan-2 {
    grid-column: span 2;
}
</style>
